
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import moment from 'moment';
    import Patient from '@/types/Patient';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import { Geschlecht } from '@/types/Geschlecht';
    import { confirmSwal, warningSwal } from '@/helper/swals';
    import { SweetAlertResult } from 'sweetalert2';
    import ElgaPatient from '@/types/soap/elga/Patient';
    import { ValidationRules } from '@/store/types';
    import {
        ListItems,
        VAutocomplete,
        VForm,
        VTextField,
        createAutocompleteFilter,
        VDatePickerInput
    } from '@/types/Components';
    import { CardInfo } from '@/types/soap/elga/Card';
    import { LoadPersonenPayload } from '@/store/personen';
    import { LoadVersicherungenPayload } from '@/store/versicherungen';
    import Versicherung from '@/types/Versicherung';
    import Land from '@/types/Land';
    import Postleitzahl from '@/types/Postleitzahl';
    import DatePickerInput from '@/components/DatePickerInput.vue';
    import {SystemEinstellung, SystemEinstellungKey} from '@/types/Einstellung';

    @Component({
        components: {
            EingetragenAmVom,
            DatePickerInput,
        },
        computed: {
            ...mapState('globals', ['rules', 'titelauswahl', 'formatDate', 'parseDate']),
            ...mapState('laender', ['laender', 'postleitzahlen']),
            ...mapState('dienstorte', ['dienstorte']),
            ...mapState('patientengruppen', ['gruppen']),
            ...mapState('patient', [
                'loaders',
                'patientMitAdresse',
                'patientMitAdresseUndPlan',
                'patientDataNew',
                'patientData',
                'dialogs',
                'elgaPatientAnlage',
                'cardInfo',
                'cardInfoPatienten',
            ]),
            ...mapState('impfstoff', ['impfstoffe']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('versicherungen', ['versicherungen']),
            ...mapState('versicherungen', { versicherungenLoaders: 'loaders' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapGetters('authentication', ['getSystemEinstellung']),
        },
        methods: {
            ...mapActions('patient', [
                'updatePatient',
                'createPatient',
                'impfungNew',
                'deletePatient',
                'searchImpfpass',
                'getCardInfo',
            ]),
            ...mapActions('globals', ['loadTitle']),
            ...mapActions('versicherungen', ['loadVersicherungen']),
            ...mapActions('personen', ['loadPersonen']),
            ...mapMutations('patient', [
                'patientDetail',
                'patientNew',
                'setDialogPatientUebertragung',
                'setDialogElgaSuche',
                'setTempPatientData',
                'setDialogSsnoSearch',
                'setDialogEcard',
            ]),
            ...mapActions('patientengruppen', ['loadPatientengruppen']),
            ...mapActions('dienstorte', ['loadDienstorte']),
        },
    })
    export default class PatientDetail extends Vue {
        @Prop() title!: string;
        @Prop({ default: false }) visible!: boolean;
        @Prop({ default: false }) isNew!: boolean;
        @Prop() value!: Patient;

        public $refs!: {
            form: VForm;
            nachname: VTextField;
            svnrLaufnummer: VTextField;
            svnrGeburtsdatum: VTextField;
            geburtsdatum: VTextField;
            versicherung: VAutocomplete;
            arzt: VAutocomplete;
            studien: VDatePickerInput;
            kontakt: VDatePickerInput;
            dsgvo_datum: VDatePickerInput;
        };

        public rules!: ValidationRules;
        public laender!: Land[];
        public postleitzahlen!: Postleitzahl[];
        public formatDate!: (input: string | null) => string | null;
        public parseDate!: (input: string | null) => string | null;
        public getCardInfo!: () => void;
        public patientMitAdresse!: boolean;
        public patientMitAdresseUndPlan!: boolean;
        public patientData!: Patient;
        public loadTitle!: () => void;
        public loadVersicherungen!: (payload?: LoadVersicherungenPayload) => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public setTempPatientData!: (patient: Patient) => void;
        public createPatient!: (payload: any) => void;
        public updatePatient!: (patient: Patient) => void;
        public impfstelle_id!: number;
        public setDialogSsnoSearch!: (visible: boolean) => void;
        public searchImpfpass!: (patient: Patient) => Promise<boolean>;
        public setDialogElgaSuche!: (visible: boolean) => void;
        public deletePatient!: (payload: { patnr: number }) => void;
        public patientDetail!: (visible: boolean) => void;
        public patientNew!: (visible: boolean) => void;
        public impfungNew!: (visible: boolean) => void;
        public cardInfoPatienten!: Patient[];
        public setDialogEcard!: (visible: boolean) => void;
        public loadPatientengruppen!: () => void;
        public loadDienstorte!: () => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;

        public data = new Patient();
        public svnrLaufnummer = '';
        public svnrGeburtsdatum = '';
        public svnrErrorMessages: string[] = [];
        public tabIndex = 'tab-allgemein';
        public defaultRestbetrag = 0;
        public hasZusatzinformation = false;

        public readonly genders: ListItems<Geschlecht> = [
            { text: 'männlich', value: Geschlecht.Maennlich },
            { text: 'weiblich', value: Geschlecht.Weiblich },
            { text: 'divers', value: Geschlecht.Divers },
            { text: 'keine Angaben', value: Geschlecht.KeineAngaben },
        ];

        public readonly versicherungenFilter = createAutocompleteFilter<Versicherung>('versicherung', 'bezeichnung');

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                if (this.patientMitAdresse) {
                    this.data = new Patient();
                    this.data.ort = this.patientData.ort;
                    this.data.strasse = this.patientData.strasse;
                    this.data.plz = this.patientData.plz;
                } else {
                    this.data = new Patient(this.value);
                    const geburstdatum = moment(this.data.geburtsdatum);
                    this.data.geburtsdatum = (geburstdatum.isValid()) ? geburstdatum.format('YYYY-MM-DD') : null;
                }

                this.defaultRestbetrag = this.data.restbetrag ?? 0;
                this.hasZusatzinformation = !!this.data.anmerkung ||
                    !!this.data.anamnese ||
                    !!this.data.diagnose ||
                    !!this.data.dsgvo_bemerkung;
                this.svnrLaufnummer = this.data.svnr ? this.data.svnr.substr(0, 4) : '';
                this.svnrGeburtsdatum = this.data.svnr ? this.data.svnr.substr(4) : '';

                this.geburtsdatumErrorMessages = [];
                this.geburtsdatumFormatted = this.formatDate(this.data.geburtsdatum);
                this.svnrErrorMessages = [];

                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }

                this.loadTitle();
                this.loadVersicherungen();
                this.loadPersonen();
                if (this.getSystemEinstellung('muw').isEnabled()) {
                    this.loadPatientengruppen();
                    this.loadDienstorte();
                }
            } else {
                this.setTempPatientData(new Patient());
                this.$nextTick(() => this.tabIndex = 'tab-allgemein');
            }
        }

        @Watch('patientData.svnr')
        public onChangeSvnr(svnr: string | null): void {
            if (this.isNew) {
                return;
            }

            this.data.svnr = svnr;
            this.svnrLaufnummer = this.data.svnr ? this.data.svnr.substr(0, 4) : '';
            this.svnrGeburtsdatum = this.data.svnr ? this.data.svnr.substr(4) : '';
            this.svnrErrorMessages = [];
        }

        @Watch('patientData.bpk_gh')
        public onChangeBpkGh(bpk_gh: string | null): void {
            if (this.isNew) {
                return;
            }

            this.data.bpk_gh = bpk_gh;
        }

        @Watch('elgaPatientAnlage')
        public onChangeElgaPatientAnlage(patient: ElgaPatient): void {
            if (patient.bPK) {
                this.data.bpk_gh = patient.bPK;
                this.data.nachname = patient.nachname;
                this.data.vorname = patient.vorname;
                this.data.titel = patient.prefix ?? null;
                this.data.titel_nach_name = patient.suffix ?? null;
                this.data.geburtsdatum = moment(patient.birthdate).format('YYYY-MM-DD');
                this.data.geschlecht = patient.geschlecht;
                this.data.ort = patient.city ?? null;
                this.data.strasse = patient.address ?? null;
                this.data.plz = patient.postalcode ?? null;
                this.data.svnr = patient.ssno;

                const land = this.laender.find(({ land }) => land === patient.country);
                if (land) {
                    this.data.land = land.land;
                }

                this.svnrLaufnummer = this.data.svnr ? this.data.svnr.substr(0, 4) : '';
                this.svnrGeburtsdatum = this.data.svnr ? this.data.svnr.substr(4) : '';
                this.svnrErrorMessages = [];

                this.geburtsdatumErrorMessages = [];
                this.geburtsdatumFormatted = this.formatDate(this.data.geburtsdatum);
            }
        }

        @Watch('cardInfo')
        public onChangeCardInfo(cardInfo: CardInfo | null): void {
            if (this.isNew && this.visible && cardInfo) {
                if (this.cardInfoPatienten.length === 0) {
                    this.data.svnr = cardInfo.number;
                    this.data.nachname = cardInfo.familyName;
                    this.data.vorname = cardInfo.givenName;
                    this.data.titel = cardInfo.prefix;
                    this.data.titel_nach_name = cardInfo.suffix;
                    this.data.geburtsdatum = cardInfo.birthDate;
                    this.data.geschlecht = cardInfo.geschlecht;

                    this.svnrLaufnummer = this.data.svnr ? this.data.svnr.substr(0, 4) : '';
                    this.svnrGeburtsdatum = this.data.svnr ? this.data.svnr.substr(4) : '';
                    this.svnrErrorMessages = [];

                    this.geburtsdatumErrorMessages = [];
                    this.geburtsdatumFormatted = this.formatDate(this.data.geburtsdatum);
                } else {
                    this.setDialogEcard(true);
                }
            }
        }

        public geburtsdatumMenu = false;
        public geburtsdatumFormatted: string | null = null;
        public geburtsdatumErrorMessages: string[] = [];

        @Watch('data.geburtsdatum')
        public onChangeGeburtsdatum(date: string | null): void {
            this.geburtsdatumFormatted = this.formatDate(this.data.geburtsdatum);

            if (date !== null) {
                this.validateGeburtsdatum();
            }

            if (this.geburtsdatumErrorMessages.length === 0 &&
                this.geburtsdatumFormatted &&
                !this.svnrGeburtsdatum) {
                this.svnrGeburtsdatum = [0, 3, 8]
                    .map((index) => this.geburtsdatumFormatted?.substr(index, 2))
                    .join('');

                if (this.svnrLaufnummer) {
                    this.validateSvnr();
                }
            }
        }

        public onInputSvnrLaufnummer(value: string): void {
            if (!this.$refs.svnrGeburtsdatum) {
                return;
            }

            if (value.length === 10) {
                this.svnrGeburtsdatum = value.slice(4);
                this.$refs.svnrGeburtsdatum.focus();
            } else if (value.length === 5 &&
                (this.$refs.svnrLaufnummer.$refs.input as HTMLInputElement).selectionStart === 5) {
                this.svnrGeburtsdatum = value.slice(4);
                this.$refs.svnrGeburtsdatum.focus();
            }
        }

        public validateGeburtsdatum(): void {
            this.geburtsdatumErrorMessages = [];

            for (const rule of [this.rules.required, this.rules.validDate]) {
                const valid = rule(this.data.geburtsdatum);
                if (valid !== true) {
                    this.geburtsdatumErrorMessages.push(valid as string);
                }
            }
        }

        public onClickAutoFillSvnr(): void {
            if (this.svnrLaufnummer.length !== 4) {
                this.svnrLaufnummer = '0000';
            }

            if (this.svnrGeburtsdatum.length !== 6) {
                if (this.geburtsdatumErrorMessages.length === 0 &&
                    this.geburtsdatumFormatted &&
                    !this.svnrGeburtsdatum) {
                    this.svnrGeburtsdatum = [0, 3, 8]
                        .map((index) => this.geburtsdatumFormatted?.substr(index, 2))
                        .join('');
                } else {
                    this.svnrGeburtsdatum = '000000';
                }
            }

            this.validateSvnr();
        }

        public onChangePostleitzahl(): void {
            const plz = this.postleitzahlen.find(({ plz }) => plz === this.data.plz);
            if (plz) {
                this.data.ort = plz.ort;
            }
        }

        public onClickSave(): void {
            this.validateGeburtsdatum();
            this.validateSvnr();

            if (this.$refs.form.validate() &&
                this.geburtsdatumErrorMessages.length === 0 &&
                this.svnrErrorMessages.length === 0) {
                this.data.svnr = this.svnr;
                this.data.impfstelle_id = this.impfstelle_id;

                if (this.isNew) {
                    if (this.patientMitAdresseUndPlan) {
                        this.createPatient({data: this.data.clone(), copy: this.patientMitAdresseUndPlan, patnr: this.patientData.patnr});
                    } else { this.createPatient({data: this.data.clone()}); }
                } else {
                    this.updatePatient(this.data.clone());
                }
            } else {
                this.tabIndex = 'tab-allgemein';
            }
        }

        public onClickPatientenregister(): void {
            this.$refs.nachname.resetValidation();
            this.setDialogSsnoSearch(true);
        }

        public onClickElgaSearch(): void {
            if (this.data.vorname && this.data.nachname && this.geburtsdatumErrorMessages.length === 0) {
                const data = this.data;

                this.searchImpfpass(data)
                    .then((success: boolean) => {
                        if (success) {
                            this.setDialogElgaSuche(true);
                            this.setTempPatientData(data);
                        }
                    });
            } else {
                warningSwal('SV-Nr. Suche', 'Ungültiger Wert bei Nachname, Vorname oder Geburtsdatum.');
            }
        }

        public onClickDelete(): void {
            confirmSwal(
                'Patient löschen',
                'Es werden die Patientendaten und alle von ELGA übernommene Impfungen gelöscht. Sind Sie sicher?',
                'Löschen',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.deletePatient({ patnr: this.data.patnr as number });
                }
            });
        }

        public onClickClose(): void {
            this.patientDetail(false);
            this.patientNew(false);
            this.impfungNew(false);
        }

        public validateSvnr(): void {
            this.svnrErrorMessages = [];

            if (this.$refs.svnrLaufnummer?.isFocused ?? true) return;
            if (this.$refs.svnrGeburtsdatum?.isFocused ?? true) return;

            const rules = [
                this.rules.required,
                this.rules.exactDigits(10),
                /*this.rules.validSvnr,*/
            ];

            for (const rule of rules) {
                const valid = rule(this.svnr);
                if (valid !== true) {
                    this.svnrErrorMessages.push(valid as string);
                }
            }

            this.data.svnr = this.svnr;
        }

        public get svnr(): string {
            return this.svnrLaufnummer + this.svnrGeburtsdatum;
        }

        public onKeydownTab(ev: KeyboardEvent, source: string): void {
            const refs = this.$refs;

            switch (source) {
                case 'svnrLaufnummer':
                case 'svnrGeburtsdatum': {
                    if (!ev.shiftKey) {
                        refs.versicherung.focus();
                    } else {
                        refs.geburtsdatum.focus();
                    }
                    break;
                }
                case 'versicherung': {
                    if (!ev.shiftKey) {
                        refs.arzt.focus();
                    } else {
                        refs.svnrLaufnummer.focus();
                    }
                    break;
                }
                default:
                    break;
            }
        }

        public onKeydownRight(ev: KeyboardEvent): void {
            if (((ev.target as HTMLInputElement)?.selectionStart ?? 0) >= 4) {
                ev.preventDefault();
                this.$refs.svnrGeburtsdatum.focus();
            }
        }

        public onKeydownLeft(ev: KeyboardEvent): void {
            if ((ev.target as HTMLInputElement).selectionStart === 0) {
                ev.preventDefault();
                this.$refs.svnrLaufnummer.focus();
            }
        }

        @Watch('geburtstagsMenu')
        public onChangeGeburtstagsMenu(): void {
            setTimeout(() => this.$refs.geburtsdatum.focus(), 500);
        }

        @Watch('data.nachname')
        public changePersonNachname() {
            if (this.data.nachname && this.data.nachname.length > 0) {
                this.data.nachname = this.data.nachname.charAt(0).toUpperCase() + this.data.nachname.slice(1);
            }
        }

        @Watch('data.vorname')
        public changePersonVorname() {
            if (this.data.vorname && this.data.vorname.length > 0) {
                this.data.vorname = this.data.vorname.charAt(0).toUpperCase() + this.data.vorname.slice(1);
            }
        }

        @Watch('data.strasse')
        public changePersonStrasse() {
            if (this.data.strasse && this.data.strasse.length > 0) {
                this.data.strasse = this.data.strasse.charAt(0).toUpperCase() + this.data.strasse.slice(1);
            }
        }

        @Watch('data.patientengruppe_id')
        public changePatientenGruppe() {
            if (this.data.patientengruppe_id !== 2) {
                this.data.dienstort_id = null;
            }
        }

        @Watch('data.studien_zustimmung')
        public changeStudienZustimmung() {
            if (this.data.studien_zustimmung === 'j') {
                this.data.studien_zustimmung_datum = moment().format('YYYY-MM-DD');
            } else {
                this.data.studien_zustimmung_datum = null;
            }
        }

        @Watch('data.kontakt_zustimmung')
        public changeKontaktZustimmung() {
            if (this.data.kontakt_zustimmung === 'j') {
                this.data.kontakt_zustimmung_datum = moment().format('YYYY-MM-DD');
            } else {
                this.data.kontakt_zustimmung_datum = null;
            }
        }

        @Watch('data.dsgvo_einverstaendnis')
        public changeDsgvoEinverstaendnis() {
            if (this.data.dsgvo_einverstaendnis === 'j') {
                this.data.dsgvo_datum = moment().format('YYYY-MM-DD');
            } else {
                this.data.dsgvo_datum = null;
            }
        }
    }
